import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {gsap} from "gsap";
import {KennethMustDie} from "../KennethMustDie";
import {IKMDInitData} from "../server/IKMDData";
import {FontWeight} from "@nolimitcity/slot-launcher/bin/loader/font/FontStatics";

export class KMDLimitCapButton extends PIXI.Container implements StageLayer {

    private readonly _limitCapAllowed: boolean;
    private _btn: PIXI.spine.Spine;
    private _limitCapPaused: boolean;
    private _enableTl: gsap.core.Timeline | null;
    private _text: PIXI.Text;

    constructor() {
        super();
        this.name = "KMDLimitCapButton";

        this._limitCapAllowed = KennethMustDie.apiPlugin.betLevel.hasCapWinLimitToggle();
        this.init();
    }

    public enable(flag: boolean, delay: number): void {
        if (!this._limitCapAllowed) {
            return;
        }

        if (this._enableTl) {
            this._enableTl.pause();
            this._enableTl.kill();

            this._enableTl = null;
        }

        const tl: gsap.core.Timeline = gsap.timeline();

        tl.add(() => {
            this.doEnableAction(flag);
        }, delay);

        this._enableTl = tl;
    }

    public turnOff(): void {
        if (!this._limitCapAllowed) {
            return;
        }

        this.setText("10000x");
        this._btn.state.setAnimation(0, "nolimit_off", false);
    }

    public turnOn(): void {
        if (!this._limitCapAllowed) {
            return;
        }

        this.setText("69000x");
        this._btn.state.setAnimation(0, "nolimit_on", false);
    }

    public toggleVisibility(value: boolean): void {
        if (!this._limitCapAllowed) {
            return;
        }

        this.visible = value;
        this._limitCapPaused = !value;
    }

    public onRestore(): void {
        if (!this._limitCapAllowed) {
            return;
        }

        const initData: IKMDInitData = KennethMustDie.model.initData;

        // if current mode is any of the bonus game mode OR it's going to be in bonus game
        if ((initData.mode.indexOf("FREESPIN") > -1) || (initData.mode.indexOf("NORMAL") > -1 && initData.nextMode.indexOf("FREESPIN") > -1)) {
            this.toggleVisibility(false);
        }

        // #490
        KennethMustDie.apiPlugin.betLevel.setBetLevelMode(initData.playedCapMode);

        this.doEnableAction(false);
    }

    public doEnableAction(flag: boolean): void {
        if (!this._limitCapAllowed) {
            return;
        }

        if (this._enableTl) {
            this._enableTl.pause();
            this._enableTl.kill();

            this._enableTl = null;
        }

        this._btn.interactive = flag;
        this._btn.buttonMode = flag;

        this._limitCapPaused = !flag;
    }

    private init(): void {
        if (!this._limitCapAllowed) {
            return;
        }

        const textBg: PIXI.Graphics = new PIXI.Graphics();
        textBg.beginFill(0x000000);
        textBg.drawRoundedRect(-38, -10, 76, 20, 30);
        textBg.endFill();
        textBg.position.set(680, 664);

        this._btn = new PIXI.spine.Spine(KennethMustDie.assetLoader.getSpineAsset("limitCapButton"));
        this._btn.position.set(680, 696);
        this._btn.hitArea = new PIXI.Rectangle(-30, -22.5, 60, 45);

        this.addChild(textBg, this._btn);

        this._btn.on('pointertap', () => this.toggleLimitCap());
        this.turnOn();

        this.enable(true, 0);

        if (KennethMustDie.apiPlugin.freeBets.hasFreeBets()) {
            this.doEnableAction(false);
            this._limitCapPaused = true;
        }
        KennethMustDie.apiPlugin.freeBets.events.on(KennethMustDie.apiPlugin.freeBets.FREE_BETS_START, () => {
            this.doEnableAction(false);
            this._limitCapPaused = true;
        });
        KennethMustDie.apiPlugin.freeBets.events.on(KennethMustDie.apiPlugin.freeBets.FREE_BETS_END, () => {
            this._limitCapPaused = false;
            this.enable(true, 0.3);
        });
        KennethMustDie.apiPlugin.events.on("extFreeRoundsEnded", () => {
            this._limitCapPaused = false;
            this.enable(true, 0.3);
        });
    }

    private toggleLimitCap(): void {
        if (!this._limitCapAllowed || this._limitCapPaused) {
            return;
        }

        KennethMustDie.soundPlayer.playLimitCapBtnSound();

        KennethMustDie.apiPlugin.betLevel.toggleCapWinLimit();
    }

    private setText(winTimesX: string): void {
        if (this._text) {
            this.removeChild(this._text);
            this._text.destroy();
        }

        const text: PIXI.Text = new PIXI.Text(winTimesX, new PIXI.TextStyle({
            fill: "#00e0ff",
            fontFamily: "entsans",
            wordWrap: false,
            fontWeight: FontWeight.BOLD,
            fontSize: 14,
            stroke: "#174278",
            strokeThickness: 2,
            lineJoin: "round"
        }));
        text.anchor.set(0.5);
        text.position.set(680, 664);
        this._text = text;

        const textCopy: PIXI.Text = new PIXI.Text(winTimesX, new PIXI.TextStyle({
            fill: "#00e0ff",
            fontFamily: "entsans",
            wordWrap: false,
            fontWeight: FontWeight.BOLD,
            fontSize: 16,
            stroke: "#174278",
            strokeThickness: 2,
            lineJoin: "round"
        }));
        textCopy.alpha = 0;
        textCopy.anchor.set(0.5);
        textCopy.position = text.position;
        textCopy.blendMode = PIXI.BLEND_MODES.ADD;

        this.addChild(text, textCopy, this._btn);

        gsap.to(textCopy, {
            alpha: 1,
            duration: 0.1
        });
        gsap.to(textCopy, {
            alpha: 0,
            duration: 0.1,
            delay: 0.1,
            onComplete: () => {
                this.removeChild(textCopy);
                textCopy.destroy();
            }
        });
    }
}